import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SparklesIcon } from "@heroicons/react/24/solid";
import {
  ChevronLeftIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { fetchCustomerPreviousWorks } from "../../../../api/customer";
import {
  PreviousWorkChecklistPanel,
  PreviousWorkReportPanel,
} from "./report-previous-work-panels";
import {
  facilityBadgeLabel,
  isDifferentFacility,
  previousWorksRequestParams,
} from "./previousWorksParams";
import "./report-previous-work.css";

function formatDate(raw, language) {
  if (!raw) return "";
  const key = String(raw).substring(0, 10);
  const date = new Date(`${key}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    const [y, m, d] = key.split("-");
    return d && m && y ? `${d}.${m}.${y}` : key;
  }
  try {
    return new Intl.DateTimeFormat(language || "nb-NO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return key;
  }
}

function rowDate(row) {
  return row?.finished_date || row?.order_date || row?.date || null;
}

function imageUrl(img) {
  if (!img) return null;
  const raw =
    typeof img === "string"
      ? img
      : img.thumb_url || img.url || img.image_url || img.preview_url || null;
  if (!raw) return null;
  const value = String(raw);
  return /^(https?:\/\/|\/|data:image\/)/i.test(value) ? value : null;
}

function highlightLabel(h) {
  if (typeof h === "string") return h;
  return h?.title || h?.text || null;
}


/** Case-style AI overview card (sparkles, clamp, Vis mer). */
function PreviousWorkAiOverview({ text, loading, error }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const bodyRef = useRef(null);
  const summaryText = typeof text === "string" ? text.trim() : "";
  const hasBody = Boolean(summaryText) && !loading && !error;

  useLayoutEffect(() => {
    if (!hasBody || expanded) return;
    const el = bodyRef.current;
    if (!el) {
      setIsOverflowing(false);
      return;
    }
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [hasBody, expanded, summaryText]);

  useEffect(() => {
    setExpanded(false);
  }, [summaryText]);

  const showToggle = hasBody && (expanded || isOverflowing);

  return (
    <section
      className={`rpw-ai-card ${expanded ? "is-open" : ""}`}
      aria-label={t("report.previous_work.ai_title")}
    >
      <div className="rpw-ai-head">
        <span className="rpw-ai-icon" aria-hidden="true">
          <SparklesIcon className="h-4 w-4" />
        </span>
        <span className="rpw-ai-title">{t("report.previous_work.ai_title")}</span>
        {loading ? (
          <span className="rpw-ai-generating" role="status">
            <span className="rpw-ai-dot" aria-hidden="true" />
            {t("report.previous_work.ai_generating")}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="rpw-ai-skeleton" aria-hidden="true">
          <span className="rpw-ai-skeleton-line" />
          <span className="rpw-ai-skeleton-line short" />
        </div>
      ) : error ? (
        <p className="rpw-ai-muted">{t("report.previous_work.ai_error")}</p>
      ) : !summaryText ? (
        <p className="rpw-ai-muted">{t("report.previous_work.ai_empty")}</p>
      ) : (
        <p
          ref={bodyRef}
          className={`rpw-ai-body ${expanded ? "is-expanded" : "is-clamped"}`}
        >
          {summaryText}
        </p>
      )}

      {showToggle ? (
        <div className="rpw-ai-foot">
          <span />
          <button
            type="button"
            className="rpw-ai-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded
              ? t("report.previous_work.show_less")
              : t("report.previous_work.show_more")}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function PreviousWorkJobCard({
  row,
  language,
  onOpenReport,
  onOpenChecklist,
  showFacilityBadge,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const dateLabel = formatDate(rowDate(row), language);
  const line =
    row?.ai_summary_line ||
    row?.ai_summary ||
    t("report.previous_work.fallback", { number: row?.report_number || row?.id });
  const images = Array.isArray(row?.images)
    ? row.images
    : Array.isArray(row?.preview_images)
      ? row.preview_images
      : [];
  const highlights = Array.isArray(row?.checklist_highlights)
    ? row.checklist_highlights.map(highlightLabel).filter(Boolean)
    : [];
  const excerpt = typeof row?.excerpt === "string" ? row.excerpt.trim() : "";
  const hasMore =
    Boolean(excerpt) || highlights.length > 0 || images.length > 2;
  const differentFacility = showFacilityBadge && isDifferentFacility(row);

  const visibleImages = expanded ? images : images.slice(0, 3);

  return (
    <article className={`rpw-job ${expanded ? "is-open" : ""}`}>
      <div className="rpw-job-main">
        <div className="rpw-job-meta">
          {dateLabel ? <span className="rpw-job-date">{dateLabel}</span> : null}
          {row?.report_type ? (
            <span className="rpw-job-type">{row.report_type}</span>
          ) : (
            <span className="rpw-job-type">
              {t("report.previous_work.report_label", {
                number: row?.report_number || row?.id,
              })}
            </span>
          )}
          {differentFacility ? (
            <span className="rpw-job-facility-badge" title={t("report.previous_work.different_facility_hint")}>
              {facilityBadgeLabel(row, t("report.previous_work.different_facility"))}
            </span>
          ) : null}
        </div>
        <p className="rpw-job-line">{line}</p>

        {visibleImages.length > 0 ? (
          <ul className="rpw-job-gallery" aria-label={t("report.previous_work.images")}>
            {visibleImages.map((img, idx) => {
              const src = imageUrl(img);
              if (!src) return null;
              return (
                <li key={img?.id || src || idx}>
                  <img src={src} alt="" className="rpw-job-thumb" loading="lazy" />
                </li>
              );
            })}
            {!expanded && images.length > 3 ? (
              <li className="rpw-job-more-count" aria-hidden="true">
                <PhotoIcon className="h-4 w-4" />+{images.length - 3}
              </li>
            ) : null}
          </ul>
        ) : null}

        {expanded ? (
          <div className="rpw-job-details">
            {excerpt ? <p className="rpw-job-excerpt">{excerpt}</p> : null}
            {highlights.length > 0 ? (
              <ul className="rpw-job-highlights">
                {highlights.map((label, idx) => (
                  <li key={`${label}-${idx}`}>{label}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="rpw-job-actions">
        <button
          type="button"
          className="rpw-job-action"
          onClick={() => onOpenReport?.(row)}
        >
          <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
          {t("report.previous_work.see_report")}
        </button>
        <button
          type="button"
          className="rpw-job-action rpw-job-action-secondary"
          onClick={() => onOpenChecklist?.(row)}
        >
          <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden="true" />
          {t("report.previous_work.see_checklist")}
        </button>
        {hasMore ? (
          <button
            type="button"
            className="rpw-job-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded
              ? t("report.previous_work.show_less")
              : t("report.previous_work.see_more")}
          </button>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Shared previous-work body (AI + jobs + drill-in). Used by report dialog and facility history.
 */
export function PreviousWorksBody({
  customerId,
  excludeReportId,
  customerSystemId,
  referenceSystemId,
  active,
  showFacilityBadge = false,
  emptyKey = "report.previous_work.empty",
}) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [aiOverview, setAiOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadedForRef = useRef(null);
  const [view, setView] = useState("overview");
  const [selectedRow, setSelectedRow] = useState(null);

  const resetDrill = useCallback(() => {
    setView("overview");
    setSelectedRow(null);
  }, []);

  const cacheKey = `${customerId || ""}:${excludeReportId || ""}:${customerSystemId || ""}:${referenceSystemId || ""}`;

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await fetchCustomerPreviousWorks(
        customerId,
        previousWorksRequestParams({
          excludeReportId,
          customerSystemId,
          referenceSystemId,
        })
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setAiOverview(
        typeof res?.ai_overview === "string" && res.ai_overview.trim()
          ? res.ai_overview.trim()
          : null
      );
      loadedForRef.current = cacheKey;
    } catch {
      setRows([]);
      setAiOverview(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [customerId, excludeReportId, customerSystemId, referenceSystemId, cacheKey]);

  useEffect(() => {
    if (!active) return;
    if (loadedForRef.current === cacheKey) return;
    load();
  }, [active, cacheKey, load]);

  useEffect(() => {
    if (!active) {
      resetDrill();
    }
  }, [active, resetDrill]);

  if (!customerId) {
    return (
      <p className="text-sm text-slate-500">{t(emptyKey)}</p>
    );
  }

  return (
    <div className="rpw-dialog space-y-4 text-left">
      {view !== "overview" ? (
        <div className="rpw-drill-nav">
          <button type="button" className="rpw-back" onClick={resetDrill}>
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t("report.previous_work.back_to_overview")}
          </button>
          <nav className="rpw-crumb" aria-label="Breadcrumb">
            <button type="button" className="rpw-crumb-link" onClick={resetDrill}>
              {t("report.previous_work.crumb_overview")}
            </button>
            <span className="rpw-crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="rpw-crumb-current">
              {view === "report"
                ? t("report.previous_work.crumb_report", {
                    number: selectedRow?.report_number || selectedRow?.id,
                  })
                : t("report.previous_work.crumb_checklist", {
                    number: selectedRow?.report_number || selectedRow?.id,
                  })}
            </span>
          </nav>
        </div>
      ) : null}

      {view === "overview" ? (
        <>
          <PreviousWorkAiOverview
            text={aiOverview}
            loading={loading}
            error={error && !aiOverview}
          />

          {loading && rows.length === 0 ? (
            <p className="text-sm text-slate-500">{t("Loading...")}</p>
          ) : error && rows.length === 0 ? (
            <p className="text-sm text-slate-500">
              {t("report.previous_work.load_error")}
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500">{t(emptyKey)}</p>
          ) : (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("report.previous_work.jobs_heading")}
              </h3>
              <ul className="space-y-3">
                {rows.map((row) => (
                  <li key={row.id}>
                    <PreviousWorkJobCard
                      row={row}
                      language={i18n?.language}
                      showFacilityBadge={showFacilityBadge}
                      onOpenReport={(r) => {
                        setSelectedRow(r);
                        setView("report");
                      }}
                      onOpenChecklist={(r) => {
                        setSelectedRow(r);
                        setView("checklist");
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}

      {view === "report" && selectedRow?.id ? (
        <PreviousWorkReportPanel reportId={selectedRow.id} />
      ) : null}

      {view === "checklist" && selectedRow?.id ? (
        <PreviousWorkChecklistPanel reportId={selectedRow.id} />
      ) : null}
    </div>
  );
}

/**
 * Shared PMDialog shell for previous work (report page + facility/anlegg history).
 */
export function ReportPreviousWorkDialog({
  open,
  onClose,
  customerId,
  excludeReportId,
  customerSystemId,
  referenceSystemId,
  title = "report.previous_work.dialog_title",
  showFacilityBadge = false,
  emptyKey = "report.previous_work.empty",
}) {
  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={title}
      size="5xl"
      closeOnOutsideClick
    >
      {customerId ? (
        <PreviousWorksBody
          customerId={customerId}
          excludeReportId={excludeReportId}
          customerSystemId={customerSystemId}
          referenceSystemId={referenceSystemId}
          active={open}
          showFacilityBadge={showFacilityBadge}
          emptyKey={emptyKey}
        />
      ) : null}
    </PMDialog>
  );
}

/**
 * Entry point + dialog for previous customer work on a report page.
 * Overview → in-dialog report/checklist read views (no page navigation, no second dialog).
 */
export default function ReportPreviousWork({ report }) {
  const { t } = useTranslation();
  const customerId = report?.customer?.id || report?.customer_id;
  const currentId = report?.id;
  const referenceSystemId =
    report?.system?.id || report?.system_id || report?.customer_system_id || null;
  const [open, setOpen] = useState(false);

  if (!customerId) return null;

  return (
    <section className="rpw-entry rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("report.previous_work.title")}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">{t("report.previous_work.hint")}</p>
        </div>
        <button
          type="button"
          className="rpw-entry-btn inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold text-white shadow-sm"
          onClick={() => setOpen(true)}
        >
          <ClockIcon className="h-4 w-4" aria-hidden="true" />
          {t("report.previous_work.open")}
        </button>
      </div>

      <ReportPreviousWorkDialog
        open={open}
        onClose={() => setOpen(false)}
        customerId={customerId}
        excludeReportId={currentId}
        referenceSystemId={referenceSystemId}
        showFacilityBadge={Boolean(referenceSystemId)}
      />
    </section>
  );
}
